import { type ReactNode, createContext, useContext, useMemo } from 'react';

import { DEFAULT_UI_LANGUAGE, type UiLanguageCode } from './languages';

/** Shape of the context published by {@link I18nProvider} and consumed by the hooks. */
export type I18nContextValue = {
  language: UiLanguageCode;
  setLanguage: (code: UiLanguageCode) => void;
};

const I18nContext = createContext<I18nContextValue | null>(null);

const FALLBACK_CONTEXT: I18nContextValue = {
  language: DEFAULT_UI_LANGUAGE,
  setLanguage: () => undefined,
};

/**
 * Internal context accessor: returns the current language and setter from
 * the nearest {@link I18nProvider}, or a safe default ({@link DEFAULT_UI_LANGUAGE}
 * + a no-op setter) when no provider is mounted. That fallback is what lets
 * {@link useTranslation} keep working in trees that haven't adopted the
 * provider yet — they just render the canonical English bundle.
 *
 * Most code should use {@link useTranslation} or {@link useUiLanguage}; reach
 * for this hook directly only when composing a higher-level provider.
 */
export function useI18nContext(): I18nContextValue {
  return useContext(I18nContext) ?? FALLBACK_CONTEXT;
}

type I18nProviderProps = {
  language: UiLanguageCode;
  setLanguage: (code: UiLanguageCode) => void;
  children: ReactNode;
};

/**
 * Publishes the current UI language and a setter into context for
 * {@link useTranslation} and {@link useUiLanguage} to read.
 *
 * The provider is **controlled**: the caller owns where `language` lives
 * (e.g. a localStorage cache, a server-synced React state, or both) and
 * supplies a `setLanguage` callback that persists the change. The provider
 * itself just memoises the context value and re-renders subscribers when
 * `language` changes.
 *
 * Mount near the app root, after any data provider you want to read from
 * in the wiring layer (for example, after Apollo if you sync the preference
 * to GraphQL).
 */
export function I18nProvider({
  language,
  setLanguage,
  children,
}: I18nProviderProps) {
  const value = useMemo(
    () => ({ language, setLanguage }),
    [language, setLanguage],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
